import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { PricingResponse } from '../../api/Requests';
import { useConfig } from '../../hooks/useConfig';

type BillingPeriod = 'monthly' | 'annual';

interface PriceDisplayProps {
  pricingInfo: PricingResponse;
  billingPeriod: BillingPeriod;
}

export function PriceDisplay({ pricingInfo, billingPeriod }: PriceDisplayProps) {
  const config = useConfig();
  const selectedPrice = billingPeriod === 'annual' && pricingInfo.prices.annual
    ? pricingInfo.prices.annual 
    : pricingInfo.prices.monthly;

  const monthlyEquivalent = billingPeriod === 'annual' && pricingInfo.prices.annual
    ? (pricingInfo.prices.annual.amount / 12).toFixed(2)
    : null;

  const savings = pricingInfo.savings && pricingInfo.savings > 0 ? pricingInfo.savings : 0;

  const discountPercent = config.INTRODUCTORY_DISCOUNT_PERCENT;
  const discountMonths = config.INTRODUCTORY_DISCOUNT_MONTHS;
  const hasIntroDiscount = discountPercent > 0;

  const fullAmount = selectedPrice.amount;
  const discountedMonthlyAmount = billingPeriod === 'annual' && pricingInfo.prices.annual
    ? (pricingInfo.prices.annual.amount / 12) * (1 - discountPercent / 100)
    : fullAmount * (1 - discountPercent / 100);

  return (
    <Box sx={{ mb: 3 }}>
      {hasIntroDiscount ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
            <Typography
              variant="h3"
              component="div"
              sx={{ fontWeight: 600 }}
            >
              ${discountedMonthlyAmount.toFixed(2)}
            </Typography>
            <Typography
              variant="h5"
              component="div"
              sx={{ fontWeight: 400, textDecoration: 'line-through', color: 'text.disabled' }}
            >
              ${billingPeriod === 'annual' && monthlyEquivalent ? monthlyEquivalent : fullAmount.toFixed(2)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            /month for first {discountMonths} months
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            then ${billingPeriod === 'annual' && monthlyEquivalent ? monthlyEquivalent : fullAmount.toFixed(2)}/month
            {billingPeriod === 'annual' ? ' billed annually' : ''}
          </Typography>
        </>
      ) : (
        <>
          <Typography
            variant="h3"
            component="div"
            sx={{ fontWeight: 600, mb: 0.5 }}
          >
            ${selectedPrice.amount.toFixed(0)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            per {billingPeriod === 'annual' ? 'year' : 'month'}
          </Typography>
          {billingPeriod === 'annual' && monthlyEquivalent && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              ${monthlyEquivalent}/month billed annually
            </Typography>
          )}
        </>
      )}
      {billingPeriod === 'annual' && savings > 0 && !hasIntroDiscount && (
        <Chip
          label={`Save $${savings.toFixed(0)}/year`}
          color="success"
          size="small"
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
}

